import { isEmbedChannelId } from './protocol';

export interface PlayerEmbedBootstrap {
  readonly hostOrigin: string;
  readonly channelId: string;
}

export const embedBootstrapParams = {
  mode: 'embed',
  hostOrigin: 'embedHostOrigin',
  channelId: 'embedChannel',
} as const;

export function normalizeEmbedOrigin(value: string): string {
  const input = value.trim();
  if (!input) throw new TypeError('Embed origin is required.');

  let parsed: URL;
  try {
    parsed = new URL(input);
  } catch {
    throw new TypeError('Embed origin must be an absolute URL origin.');
  }

  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
    throw new TypeError('Embed origin must use http or https.');
  }
  if (
    parsed.username ||
    parsed.password ||
    parsed.pathname !== '/' ||
    parsed.search ||
    parsed.hash
  ) {
    throw new TypeError('Embed origin must not include credentials, path, query or fragment.');
  }

  return parsed.origin;
}

export function createEmbedChannelId(): string {
  if (typeof crypto === 'undefined' || typeof crypto.randomUUID !== 'function') {
    throw new Error('Secure random UUID generation is unavailable.');
  }
  return crypto.randomUUID();
}

export function buildPlayerEmbedUrl(
  playerUrl: string,
  hostOrigin: string,
  channelId: string,
): string {
  if (!isEmbedChannelId(channelId)) throw new RangeError('Invalid embed channel id.');

  const url = new URL(playerUrl);
  if (url.protocol !== 'https:' && url.protocol !== 'http:') {
    throw new TypeError('Player URL must use http or https.');
  }
  if (url.username || url.password) {
    throw new TypeError('Player URL must not contain credentials.');
  }

  url.searchParams.set(embedBootstrapParams.mode, '1');
  url.searchParams.set(embedBootstrapParams.hostOrigin, normalizeEmbedOrigin(hostOrigin));
  url.searchParams.set(embedBootstrapParams.channelId, channelId);
  return url.toString();
}

export function parsePlayerEmbedBootstrap(urlValue: string): PlayerEmbedBootstrap | null {
  let url: URL;
  try {
    url = new URL(urlValue);
  } catch {
    return null;
  }

  if (url.searchParams.get(embedBootstrapParams.mode) !== '1') return null;

  const hostOrigin = url.searchParams.get(embedBootstrapParams.hostOrigin);
  const channelId = url.searchParams.get(embedBootstrapParams.channelId);
  if (!hostOrigin || !channelId || !isEmbedChannelId(channelId)) return null;

  try {
    return {
      hostOrigin: normalizeEmbedOrigin(hostOrigin),
      channelId,
    };
  } catch {
    return null;
  }
}
